import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { createUser, deleteUser, listUsers, updateUser } from "../api/users";
import { listWorkCenterGroups } from "../api/workCenterGroups";
import { EmptyState, PageHeader, StatusBadge } from "../components/ui";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "quality", label: "Kalite" },
  { value: "operator", label: "Operator" },
  { value: "depocu", label: "Depocu" },
  { value: "bakimci", label: "Bakımcı" },
  { value: "yonetici", label: "Yönetici" },
  { value: "uretim_muduru", label: "Üretim Müdürü" },
];

function emptyForm() {
  return {
    name: "",
    email: "",
    username: "",
    employee_code: "",
    password: "",
    role: "operator",
    work_center_group_ids: [],
  };
}

function roleLabel(role) {
  return ROLE_OPTIONS.find((item) => item.value === role)?.label || role || "-";
}

export default function Users() {
  const [items, setItems] = useState([]);
  const [workCenterGroups, setWorkCenterGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [userRows, groupRows] = await Promise.all([
        listUsers(),
        listWorkCenterGroups(),
      ]);
      setItems(Array.isArray(userRows) ? userRows : []);
      setWorkCenterGroups(Array.isArray(groupRows) ? groupRows : []);
    } catch (err) {
      console.error("USERS LOAD ERROR", err);
      setError(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Kullanicilar yuklenemedi."
      );
      setItems([]);
      setWorkCenterGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setSelected(null);
    setForm(emptyForm());
    setSaveError("");
    setDialogOpen(true);
  }

  function openEdit(user) {
    setSelected(user);
    setForm({
      name: user?.name ? String(user.name) : "",
      email: user?.email ? String(user.email) : "",
      username: user?.username ? String(user.username) : "",
      employee_code: user?.employee_code ? String(user.employee_code) : "",
      password: "",
      role: user?.role ? String(user.role) : "operator",
      // Eski tekil alana düşüş: API henüz güncellenmemişse de form dolu gelsin.
      work_center_group_ids: Array.isArray(user?.work_center_group_ids)
        ? user.work_center_group_ids.map((x) => String(x))
        : user?.work_center_group_id
          ? [String(user.work_center_group_id)]
          : [],
    });
    setSaveError("");
    setDialogOpen(true);
  }

  function closeDialog() {
    if (saving) return;
    setDialogOpen(false);
    setSelected(null);
    setForm(emptyForm());
    setSaveError("");
  }

  function setField(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "role" && value !== "operator") {
        next.work_center_group_ids = [];
      }
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim() || null,
        username: String(form.username || "").trim(),
        employee_code: String(form.employee_code || "").trim() || null,
        role: String(form.role || "").trim(),
        password: String(form.password || ""),
        work_center_group_ids:
          String(form.role) === "operator"
            ? (form.work_center_group_ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0)
            : [],
      };

      if (selected?.id) {
        await updateUser(selected.id, payload);
      } else {
        await createUser(payload);
      }
      closeDialog();
      await load();
    } catch (err) {
      console.error("USER SAVE ERROR", err);
      setSaveError(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Kullanici kaydedilemedi."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(user) {
    if (!window.confirm(`${user?.name || "Bu kullanici"} pasife alinsin mi?`)) return;
    try {
      await deleteUser(user.id);
      await load();
    } catch (err) {
      console.error("USER DELETE ERROR", err);
      alert(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Kullanici pasife alinamadi."
      );
    }
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Kullanicilar"
        subtitle="Giris bilgilerini, yetki rolunu ve operator is merkezi grubunu yonetin."
        actions={
          <Button variant="contained" onClick={openCreate}>
            + Yeni Kullanici
          </Button>
        }
      />

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && !items.length ? (
        <EmptyState
          description="Henüz kullanici yok."
          actionLabel="+ Yeni Kullanici"
          onAction={openCreate}
        />
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ad</TableCell>
                <TableCell>Giris</TableCell>
                <TableCell>Yetki</TableCell>
                <TableCell>Is Merkezi Grubu</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell align="right">Islemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 700 }}>{user.name || "-"}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{user.username || "-"}</TableCell>
                  <TableCell>{user.employee_code || "-"}</TableCell>
                  <TableCell>{roleLabel(user.role)}</TableCell>
                  <TableCell>
                    {(user.work_center_groups || []).length ? (
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {user.work_center_groups.map((g) => (
                          <Chip key={g.id} size="small" label={g.code || g.name || g.id} />
                        ))}
                      </Stack>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge value={user.status || "-"} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => openEdit(user)}>
                        Duzenle
                      </Button>
                      <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(user)}>
                        Pasife Al
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={saving ? undefined : closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{selected?.id ? "Kullanici Duzenle" : "Yeni Kullanici"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Ad Soyad"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={saving}
            />
            <TextField
              label="E-posta (opsiyonel)"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              disabled={saving}
            />
            <TextField
              label="Giris Kullanici Adi"
              value={form.username}
              onChange={(e) => setField("username", e.target.value)}
              disabled={saving}
            />
            <TextField
              label="Calisan Kodu"
              value={form.employee_code}
              onChange={(e) => setField("employee_code", e.target.value)}
              disabled={saving}
            />
            <TextField
              label="Yetki"
              select
              value={form.role}
              onChange={(e) => setField("role", e.target.value)}
              disabled={saving}
            >
              {ROLE_OPTIONS.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>
            {form.role === "operator" ? (
              <TextField
                label="Is Merkezi Gruplari"
                select
                SelectProps={{
                  multiple: true,
                  renderValue: (selectedIds) => {
                    const list = (selectedIds || [])
                      .map((id) => workCenterGroups.find((g) => String(g.id) === String(id)))
                      .filter(Boolean);
                    if (!list.length) return "";
                    return (
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {list.map((g) => (
                          <Chip key={g.id} size="small" label={g.code || g.name || g.id} />
                        ))}
                      </Stack>
                    );
                  },
                }}
                value={form.work_center_group_ids}
                onChange={(e) => setField("work_center_group_ids", e.target.value)}
                disabled={saving}
                helperText="Birden fazla grup secebilirsiniz. Operator secili gruplarin tum is merkezlerini gorur."
              >
                {workCenterGroups.map((group) => (
                  <MenuItem key={group.id} value={String(group.id)}>
                    <Checkbox
                      checked={form.work_center_group_ids.some((x) => String(x) === String(group.id))}
                      size="small"
                    />
                    <ListItemText primary={`${group.code || ""} ${group.name || ""}`.trim()} />
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              label={selected?.id ? "Yeni Sifre (bos birakirsan degismez)" : "Sifre"}
              type="password"
              value={form.password}
              onChange={(e) => setField("password", e.target.value)}
              disabled={saving}
            />
            {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit" disabled={saving}>
            Iptal
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
